import React, { useEffect, useState } from 'react';
import { Modal, Table, Tag, Typography, Select, Spin, Alert } from 'antd';
import { inventoryService, InventoryLot } from '../../services/inventoryService';
import { formatDate, getDaysUntilExpiry, getExpiryColor } from '../../utils/formatters';

const { Title, Text } = Typography;
const { Option } = Select;

interface ExpiringMedicinesModalProps {
  visible: boolean;
  onClose: () => void;
}

const ExpiringMedicinesModal: React.FC<ExpiringMedicinesModalProps> = ({
  visible,
  onClose
}) => {
  const [loading, setLoading] = useState(false);
  const [expiringMedicines, setExpiringMedicines] = useState<InventoryLot[]>([]);
  const [daysFilter, setDaysFilter] = useState(90);

  useEffect(() => {
    if (visible) {
      loadExpiringMedicines();
    }
  }, [visible, daysFilter]);

  const loadExpiringMedicines = async () => {
    setLoading(true);
    try {
      const data = await inventoryService.getExpiring(daysFilter);
      setExpiringMedicines(data);
    } catch (error) {
      console.error('Error loading expiring medicines:', error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Tên thuốc',
      dataIndex: 'medicine_name',
      key: 'medicine_name',
      width: 200,
      render: (name: string, record: InventoryLot) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{name}</div>
          <Text type="secondary">{record.medicine_code}</Text>
        </div>
      ),
    },
    {
      title: 'Số lô',
      dataIndex: 'lot_number',
      key: 'lot_number',
      width: 120,
    },
    {
      title: 'Hạn sử dụng',
      dataIndex: 'expiry_date',
      key: 'expiry_date',
      width: 120,
      render: (date: string) => {
        const daysUntilExpiry = getDaysUntilExpiry(date);
        const color = getExpiryColor(daysUntilExpiry);
        
        return (
          <Tag color={color}>
            {formatDate(date)}
            <br />
            <small>{daysUntilExpiry} ngày</small>
          </Tag>
        );
      },
    },
    {
      title: 'Số lượng',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      align: 'right' as const,
      render: (quantity: number, record: InventoryLot) => (
        <Text strong>
          {quantity} {record.unit}
        </Text>
      ),
    },
    {
      title: 'Giá nhập',
      dataIndex: 'cost_price',
      key: 'cost_price',
      width: 120,
      align: 'right' as const,
      render: (price: number) => {
        if (!price) return 'N/A';
        return new Intl.NumberFormat('vi-VN', {
          style: 'currency',
          currency: 'VND',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(price);
      },
    },
    {
      title: 'Nhà cung cấp',
      dataIndex: 'supplier_name',
      key: 'supplier_name',
      width: 150,
    },
    {
      title: 'Ngày nhập',
      dataIndex: 'import_date',
      key: 'import_date',
      width: 120,
      render: (date: string) => formatDate(date),
    },
  ];

  const getSummaryStats = () => {
    const totalItems = expiringMedicines.length;
    const totalQuantity = expiringMedicines.reduce((sum, item) => sum + item.quantity, 0);
    const criticalItems = expiringMedicines.filter(item => getDaysUntilExpiry(item.expiry_date) <= 30).length;
    const warningItems = expiringMedicines.filter(item => {
      const days = getDaysUntilExpiry(item.expiry_date);
      return days > 30 && days <= 90;
    }).length;

    return (
      <div style={{ marginBottom: 16 }}>
        <Alert
          message={`Tổng cộng: ${totalItems} lô thuốc sắp hết hạn trong ${daysFilter} ngày tới`}
          description={
            <div>
              <Text>Số lượng tổng: {totalQuantity.toLocaleString('vi-VN')}</Text>
              <br />
              <Text type="danger">Hết hạn trong 30 ngày: {criticalItems} lô</Text>
              <br />
              <Text type="warning">Hết hạn trong 90 ngày: {warningItems} lô</Text>
            </div>
          }
          type="warning"
          showIcon
        />
      </div>
    );
  };

  return (
    <Modal
      title={
        <div>
          <Title level={4} style={{ margin: 0 }}>
            Thuốc sắp hết hạn
          </Title>
          <div style={{ marginTop: 8 }}>
            <Text type="secondary">Hiển thị thuốc hết hạn trong: </Text>
            <Select
              value={daysFilter}
              onChange={setDaysFilter}
              style={{ width: 100, marginLeft: 8 }}
            >
              <Option value={30}>30 ngày</Option>
              <Option value={60}>60 ngày</Option>
              <Option value={90}>90 ngày</Option>
              <Option value={180}>180 ngày</Option>
            </Select>
          </div>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1000}
      destroyOnClose
    >
      <Spin spinning={loading}>
        {getSummaryStats()}
        
        <Table
          dataSource={expiringMedicines}
          columns={columns}
          rowKey="id"
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} của ${total} lô thuốc`
          }}
          size="small"
          scroll={{ x: 900 }}
        />
      </Spin>
    </Modal>
  );
};

export default ExpiringMedicinesModal; 